<script setup lang="ts">
/**
 * 详情页「更多+」共享链接（F10，Q15）。
 *
 * 填充 `SectionCard` / `GridItem` 的 `more` slot，跳 `/detail/:sectionId/:slug?`。
 * **`bodyHtml` 非空才渲染**（避免空详情入口，PRD §305）——调用方无需自行
 * 判空，组件内置 `hasText(bodyHtml)` 守卫。
 *
 * **不读 store**：预览 token 由父（HomePage / Section 容器）透传 `previewToken`
 * prop，URL 在预览态携 `?preview=` query（F10 基础透传，F15 闭合全量 Material）。
 * 保持与 F08 Section 纯渲染原则一致——Section 组件不直接 `useShowcase`。
 *
 * **挂载范围**：F05 的 7 个完整内容 Section（intro / video / innovation 等）
 * 首页即全文，不挂本组件；F12 `ProblemSection` / `MethodSection` 卡片摘要
 * 形态复用本组件作「更多+」入口。
 */
import { computed } from 'vue';
import { RouterLink } from 'vue-router';

import { detailRouteLocation } from '@/composables/detail-path';
import { moduleRouteLocation } from '@/composables/module-target';
import { parseTarget } from '@/composables/nav-target';
import { copy } from '@/i18n/zh-CN';
import { hasText } from '@/registry/sections/section-presenters';
import type { PublicSectionView } from '@/api/types';

const props = defineProps<{
  readonly section: PublicSectionView;
  readonly anchor?: string | null;
  readonly targetAnchor?: string | null;
  /** 预览态 token，由父透传；非空则 URL 携 ?preview= query。 */
  readonly previewToken?: string | null;
}>();

const visible = computed(() => hasText(props.section.bodyHtml));
const moduleTarget = computed(() => {
  if (!hasText(props.targetAnchor ?? '')) {
    return null;
  }
  const target = parseTarget(props.targetAnchor!);
  return target.kind === 'module' ? target : null;
});
const to = computed(() => {
  if (moduleTarget.value !== null) {
    return moduleRouteLocation(moduleTarget.value.routeName, moduleTarget.value.moduleId, {
      anchor: moduleTarget.value.anchor,
      previewToken: props.previewToken,
    });
  }
  return detailRouteLocation(props.section, {
    anchor: props.anchor,
    previewToken: props.previewToken,
  });
});
</script>

<template>
  <RouterLink
    v-if="visible"
    :to="to"
    :class="$style.link"
    data-testid="detail-more-link"
  >
    {{ copy.detail.more }}
  </RouterLink>
</template>

<style module>
.link {
  flex: 0 0 auto;
  align-self: flex-end;
  margin-bottom: var(--template-title-icon-gap);
  color: var(--color-brand-accent);
  font-size: var(--font-size-sm);
}

.link:hover {
  color: var(--color-brand-primary);
}
</style>
